/* A single owner for desktop disclosures and the native mobile navigation dialog. */
(() => {
  if (customElements.get('ruemood-navigation')) return;
  class RuemoodNavigation extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      const on = (target, event, listener, options = {}) => target?.addEventListener(event, listener, {...options, signal:this.controller.signal});
      const header = this.querySelector('.rn-header');
      const mobile = this.querySelector('.rn-mobile');
      const menuButton = this.querySelector('[data-menu-open]');
      const shade = this.querySelector('[data-nav-shade]');
      const triggers = [...this.querySelectorAll('[data-nav-group]')];
      const panels = [...this.querySelectorAll('.rn-panel')];
      const disclosures = [...this.querySelectorAll('[data-mobile-group]')];
      const desktop = matchMedia('(min-width:1100px)');
      const finePointer = matchMedia('(hover:hover) and (pointer:fine)');
      let active = null, hoverOpened = false;
      this.mobile = mobile;
      const clearTimers = () => { clearTimeout(this.hoverTimer); clearTimeout(this.closeTimer); clearTimeout(this.focusTimer); };
      const closeDesktop = (restore = false) => {
        clearTimers(); const old = active; active = null; hoverOpened = false;
        triggers.forEach(b => b.setAttribute('aria-expanded', 'false'));
        panels.forEach(p => p.hidden = true); shade.hidden = true;
        if (restore && old) old.focus();
      };
      const openDesktop = (button, hover = false) => {
        if (!desktop.matches) return;
        clearTimers(); active = button; hoverOpened = hover;
        triggers.forEach(b => b.setAttribute('aria-expanded', String(b === button)));
        panels.forEach(p => p.hidden = p.id !== button.getAttribute('aria-controls'));
        shade.hidden = false;
      };
      triggers.forEach(button => {
        on(button, 'click', () => active === button && !hoverOpened ? closeDesktop() : openDesktop(button));
        on(button, 'pointerenter', () => {
          if (!finePointer.matches) return;
          clearTimeout(this.hoverTimer);
          this.hoverTimer = setTimeout(() => openDesktop(button, true), 150);
        });
        on(button, 'pointerleave', () => clearTimeout(this.hoverTimer));
        on(button, 'keydown', e => {
          if (e.key !== 'ArrowDown') return;
          e.preventDefault(); openDesktop(button);
          this.querySelector('#' + CSS.escape(button.getAttribute('aria-controls')))?.querySelector('a')?.focus();
        });
      });
      on(header, 'pointerenter', () => clearTimeout(this.closeTimer));
      on(header, 'pointerleave', () => {
        if (!finePointer.matches) return;
        clearTimeout(this.closeTimer); this.closeTimer = setTimeout(() => closeDesktop(), 250);
      });
      on(header, 'focusout', e => {
        // Focus can briefly be the body between pointerdown and the link receiving focus.
        // Do not remove that link before its click can navigate.
        if (e.relatedTarget && header.contains(e.relatedTarget)) return;
        clearTimeout(this.focusTimer);
        this.focusTimer = setTimeout(() => {
          if (active && !header.contains(document.activeElement)) closeDesktop();
        }, 0);
      });
      on(shade, 'click', () => closeDesktop());
      on(document, 'keydown', e => {
        if (e.key === 'Escape' && active) { e.preventDefault(); closeDesktop(true); }
      });
      on(document, 'click', e => { if (active && !header.contains(e.target)) closeDesktop(); });
      // Existing Search V2 and cart-drawer-v2 handle these clicks. Only dismiss navigation.
      on(header, 'click', e => {
        if (e.target.closest('[data-search-v2-toggle],[data-cart-button]')) closeDesktop();
      }, {capture:true});
      on(menuButton, 'click', () => {
        closeDesktop(); mobile.showModal();
        menuButton.setAttribute('aria-expanded', 'true');
        document.body.classList.add('rn-scroll-lock');
        mobile.querySelector('[data-menu-close]').focus();
      });
      on(mobile.querySelector('[data-menu-close]'), 'click', () => mobile.close());
      on(mobile, 'close', () => {
        menuButton.setAttribute('aria-expanded', 'false');
        document.body.classList.remove('rn-scroll-lock');
        if (this.isConnected && !desktop.matches) menuButton.focus();
      });
      on(mobile, 'click', e => {
        if (e.target !== mobile) return;
        const r = mobile.getBoundingClientRect();
        if (e.clientX < r.left || e.clientX > r.right || e.clientY < r.top || e.clientY > r.bottom) mobile.close();
      });
      disclosures.forEach(button => on(button, 'click', () => {
        const opening = button.getAttribute('aria-expanded') !== 'true';
        disclosures.forEach(b => {
          const expanded = b === button && opening;
          b.setAttribute('aria-expanded', String(expanded));
          this.querySelector('#' + CSS.escape(b.getAttribute('aria-controls'))).hidden = !expanded;
        });
      }));
      on(desktop, 'change', e => { closeDesktop(); if (e.matches && mobile.open) mobile.close(); });
      on(window, 'pagehide', () => { closeDesktop(); if (mobile.open) mobile.close(); });
      // Collection links stay ordinary server-rendered links, including locale prefixes.
      const path = location.pathname.replace(/\/$/, '') || '/';
      this.querySelectorAll('a[href]').forEach(a => {
        if ((new URL(a.href, location.href).pathname.replace(/\/$/, '') || '/') !== path || path === '/') return;
        a.setAttribute('aria-current', 'page');
        const panel = a.closest('.rn-panel');
        if (panel) triggers.find(t => t.getAttribute('aria-controls') === panel.id)?.setAttribute('data-current', 'true');
      });
      this.closeDesktop = closeDesktop;
    }
    disconnectedCallback() {
      this.closeDesktop?.();
      if (this.mobile?.open) {
        this.mobile.close(); document.body.classList.remove('rn-scroll-lock');
      }
      this.controller?.abort(); this.controller = null;
    }
  }
  customElements.define('ruemood-navigation', RuemoodNavigation);
})();
